import type {
    VoteType,
} from "@mrflark/spl_sdk";
import {
    Heart,
    Share2,
    SkipBack,
    SkipForward,
    ThumbsDown,
    ThumbsUp,
    Trash2,
} from "lucide-preact";
import type {
    ReactElement,
} from "preact/compat";

import {
    Button,
} from "@mrflark/ui";
import {
    useAuthStore,
} from "../../stores/authStore.ts";
import {
    Tooltip,
} from "../common/Tooltip.tsx";

type ViewerActionBarProps = {
    onPrevious: () => void;
    onVote: (voteType: VoteType) => void;
    onNext: () => void;
    onDelete: () => void;
    onShare: () => void;
    canGoPrevious: boolean;
    isLoading: boolean;
    hasItem: boolean;
    votePending: boolean;
    deletePending: boolean;
    currentVote: VoteType | null;
};

export const ViewerActionBar = ({
    onPrevious,
    onVote,
    onNext,
    onDelete,
    onShare,
    canGoPrevious,
    isLoading,
    hasItem,
    votePending,
    deletePending,
    currentVote,
}: ViewerActionBarProps): ReactElement => {
    const {
        canDelete,
    } = useAuthStore();

    return (
        <div className="mt-6 flex items-center gap-4">
            <Tooltip content="Go back in history. Shortcut: P or Left arrow">
                <Button
                    onClick={onPrevious}
                    disabled={isLoading || !canGoPrevious}
                    variant="secondary"
                    className="gap-2"
                >
                    <SkipBack size={18} />
                    Previous
                </Button>
            </Tooltip>
            <Tooltip content="Like. Shortcut: V">
                <Button
                    aria-label="Like"
                    onClick={() => onVote("like")}
                    disabled={!hasItem || votePending}
                    variant={currentVote === "like" ? "success" : "secondary"}
                    className="gap-1.5"
                >
                    <ThumbsUp size={18} />
                </Button>
            </Tooltip>
            <Tooltip content="Dislike">
                <Button
                    aria-label="Dislike"
                    onClick={() => onVote("dislike")}
                    disabled={!hasItem || votePending}
                    variant={currentVote === "dislike" ? "error" : "secondary"}
                    className="gap-1.5"
                >
                    <ThumbsDown size={18} />
                </Button>
            </Tooltip>
            <Tooltip content="Love">
                <Button
                    aria-label="Love"
                    onClick={() => onVote("love")}
                    disabled={!hasItem || votePending}
                    variant="secondary"
                    className={`gap-1.5 ${
                        currentVote === "love"
                            ? "bg-rose-500/20 text-rose-400 border-rose-500/30 hover:bg-rose-500/30"
                            : ""
                    }`}
                >
                    <Heart size={18} />
                </Button>
            </Tooltip>
            <Tooltip content="Create share link. Shortcut: S">
                <Button
                    aria-label="Share"
                    onClick={onShare}
                    disabled={!hasItem}
                    variant="secondary"
                    className="gap-1.5"
                >
                    <Share2 size={18} />
                </Button>
            </Tooltip>
            <Tooltip content="Load next item. Shortcut: Space or Right arrow">
                <Button
                    onClick={onNext}
                    disabled={isLoading}
                    className="gap-2"
                >
                    <SkipForward size={18} />
                    Next
                </Button>
            </Tooltip>
            {canDelete() && (
                <Tooltip content="Soft-delete this item">
                    <Button
                        onClick={onDelete}
                        disabled={!hasItem || deletePending}
                        variant="error"
                        className="gap-2"
                    >
                        <Trash2 size={18} />
                        Delete
                    </Button>
                </Tooltip>
            )}
        </div>
    );
};
